import { useEffect, useMemo, useState } from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import { capitalize, startCase } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { i18n, useTranslation } from 'next-i18next';

import { getPageDocument } from '@/services/apis';
import CMCCloudBreadcrumbs from '@/components/breadcrums';
import Image from 'next/image';

function DocumentHomePage({ sidebarData, productData = [] }) {
  const { t } = useTranslation(['document']);
  const router = useRouter();
  const productId = router?.query?.productId;
  const productName = startCase(productId?.[0]?.replaceAll('-', ' '));
  const product = productData?.[0]?.attributes || {};
  const banner = product?.banners?.[0];
  const bannerImage = banner?.image?.data?.attributes?.url;
  const productDescription = product?.description || banner?.content || product?.overview?.overview;
  const [faqGroups, setFaqGroups] = useState([]);
  const documentCategories = (sidebarData || []).filter((item) => {
    const category = item?.attributes || {};
    return !/faq|frequently|cau-hoi|hoi-dap/i.test(`${category.slug || ''} ${category.name || ''}`);
  });
  const faqCategory = (sidebarData || []).find((item) => {
    const category = item?.attributes || {};
    return /faq|frequently|cau-hoi|hoi-dap/i.test(`${category.slug || ''} ${category.name || ''}`);
  });
  const faqPage = faqCategory?.attributes?.document_pages?.data?.[0]?.attributes;
  const faqDetailHref = faqPage
    ? `/document/${productId?.[0]}/${faqCategory?.attributes?.slug}/${faqPage.slug}`
    : '';
  const viewAllStartLink = `/document/${productId?.[0]}/${sidebarData[0]?.attributes?.slug}/${sidebarData[0]?.attributes?.document_pages?.data[0]?.attributes?.slug}`;
  const viewAllInstructionLink = `/document/${productId?.[0]}/${sidebarData[1]?.attributes?.slug}/${sidebarData[1]?.attributes?.document_pages?.data[0]?.attributes?.slug}`;
  const { language } = i18n;
  const breadcrumbs = useMemo(() => {
    return [
      {
        label: t('document', { ns: 'document' }),
        href: `/document`,
      },
      {
        label: capitalize(productId?.[0]?.replaceAll('-', ' ')),
        href: `/document/${productId?.[0]}`,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    if (!faqPage?.slug || !productId?.[0]) {
      setFaqGroups([]);
      return undefined;
    }

    let isCurrent = true;
    getPageDocument({
      featureSlug: faqPage.slug,
      locale: router.locale,
      productSlug: productId[0],
    })
      .then((pages) => {
        if (!isCurrent) return;

        const content = pages?.[0]?.attributes?.content;
        if (!content || typeof window === 'undefined') {
          setFaqGroups([]);
          return;
        }

        const parsedContent = new window.DOMParser().parseFromString(content, 'text/html');
        const headings = Array.from(parsedContent.querySelectorAll('h1, h2, h3'));
        const groups = [];
        let currentGroup;

        headings.forEach((heading, index) => {
          const title = heading.textContent?.trim();
          if (!title) return;

          if (Number(heading.tagName.slice(1)) <= 2) {
            currentGroup = { key: `group-${index}`, title, questions: [] };
            groups.push(currentGroup);
          } else {
            if (!currentGroup) {
              currentGroup = {
                key: 'group-default',
                title: faqCategory?.attributes?.name || '',
                questions: [],
              };
              groups.push(currentGroup);
            }

            currentGroup.questions.push({
              title,
              href: `${faqDetailHref}#document-${index}`,
            });
          }
        });

        setFaqGroups(groups.filter((group) => group.questions.length));
      })
      .catch(() => {
        if (isCurrent) setFaqGroups([]);
      });

    return () => {
      isCurrent = false;
    };
  }, [faqCategory, faqDetailHref, faqPage?.slug, productId, router.locale]);

  return (
    <Box
      sx={{
        height: '100%',
        px: { xs: 2, md: 3 },
        py: { xs: 2 },
        color: '#231F20',
        backgroundImage: `url("/images/document-homepage.png")`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <CMCCloudBreadcrumbs items={breadcrumbs} sx={{ pl: 0, mb: 1.5 }} />
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          minHeight: { xs: 280, md: 340 },
          mb: { xs: 4, md: 6 },
          px: { xs: 2, md: 4 },
          py: { xs: 4, md: 6 },
          overflow: 'hidden',
          borderRadius: '8px',
          backgroundColor: '#f0f9fc',
          background: bannerImage
            ? `linear-gradient(90deg, rgba(89, 89, 89, 0.20) 0%, rgba(192, 192, 192, 0.16) 35%, rgba(240, 249, 252, 0.00) 70%), url("${bannerImage}") lightgray 50% / cover no-repeat`
            : 'linear-gradient(120deg, #f0f9fc 0%, #e4f5fa 100%)',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 720 }}>
          <Typography
            variant="h1"
            sx={{
              mb: 1,
              color: '#fff',
              fontSize: { xs: 30, md: 32 },
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            {product?.name || productName}
          </Typography>
          {productDescription && (
            <Typography
              sx={{ maxWidth: 500, fontWeight: 400, color: '#fff', fontSize: 14, lineHeight: 1.6 }}
            >
              {productDescription}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
            {banner?.buttons?.slice(0, 1).map((button, index) => (
              <Button
                key={index}
                component="a"
                href="https://portalv2.cloud.cmctelecom.vn/"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  minHeight: 42,
                  px: 2,
                  backgroundColor: '#fff',
                  color: '#0058A8',
                  fontWeight: 600,
                  fontSize: 14,
                  textTransform: 'none',
                  borderRadius: '8px',
                  boxShadow: '0px 1px 2px 0px #231F200D',
                  '&:hover': { backgroundColor: '#0058A8', color: '#fff' },
                }}
              >
                {language === 'vi'
                  ? `${button?.name} ${t('product', { ns: 'common' }).toLowerCase()}`
                  : `Try out the ${t('product', { ns: 'common' }).toLowerCase()}`}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" sx={{ mb: { xs: 5, md: 7 } }}>
        <Typography variant="h2" sx={{ mb: 3, color: '#231F20', fontSize: 20, fontWeight: 600 }}>
          {router.locale === 'vi' ? 'Danh mục tài liệu' : 'Document categories'}
        </Typography>
        <Grid container spacing={2}>
          {documentCategories.map((item, index) => {
            const { name, document_pages: documentsPage = {} } = item?.attributes || {};
            const documents = (documentsPage?.data || []).slice(0, 12);
            let mdColumns = 6;
            let viewAllLink = '';
            if (index === 0) {
              mdColumns = 4;
              viewAllLink = viewAllStartLink;
            } else if (index === 1) {
              mdColumns = 8;
              viewAllLink = viewAllInstructionLink;
            }
            return (
              <Grid item xs={12} md={mdColumns} key={item.id} id={`document-category-${item.id}`}>
                <Box
                  sx={{
                    height: '100%',
                    p: 2,
                    border: '1px solid #ADE9FD',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 2px 0px #231F200F',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.25,
                      mb: 1.5,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: 54,
                        height: 2,
                        backgroundColor: '#0C7FBE',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                      }}
                    >
                      <Typography sx={{ color: '#0058A8', fontSize: 24, fontWeight: 700 }}>
                        {String(index + 1).padStart(2, '0')}
                      </Typography>
                      <Typography sx={{ color: '#231F20', fontSize: 16, fontWeight: 600 }}>
                        {name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        '&:hover a': {
                          backgroundColor: '#F2F2F2',
                          color: '#231F20 !important',
                        },
                      }}
                    >
                      <Link
                        href={viewAllLink}
                        style={{
                          color: '#4F4A4B',
                          fontWeight: 600,
                          fontSize: 14,
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          transition: 'all .3s',
                        }}
                      >
                        {router.locale === 'vi' ? 'Xem tất cả' : 'View all'}
                        <Image
                          style={{ display: 'inline-block', marginLeft: '6px' }}
                          src="/images/chevron-right.svg"
                          alt="chevron-right"
                          width={6}
                          height={10}
                        />
                      </Link>
                    </Box>
                  </Box>
                  <Grid container columnSpacing={2} rowSpacing={1}>
                    {documents.map((document) => {
                      const length = documents.length;
                      const colSize = length <= 4 ? 12 : length > 4 && length <= 8 ? 6 : 4;
                      return (
                        <Grid
                          sx={{
                            transition: 'all .3s',
                            '&:hover a': {
                              color: '#0058A8 !important',
                            },
                            '&:hover span.title': {
                              textDecoration: 'underline !important',
                            },
                          }}
                          item
                          xs={12}
                          sm={colSize}
                          key={document.id}
                        >
                          <Link
                            href={`/document/${productId?.[0]}/${item?.attributes?.slug}/${document?.attributes?.slug}`}
                            style={{
                              color: '#14A8E4',
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 8,
                              fontSize: 14,
                              fontWeight: 500,
                              lineHeight: 1.6,
                              textDecoration: 'none',
                            }}
                          >
                            <span aria-hidden="true">•</span>
                            <span
                              class="title"
                              title={document?.attributes?.title}
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {document?.attributes?.title}
                            </span>
                          </Link>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {!!faqGroups.length && (
        <Box component="section" sx={{ mb: { xs: 5, md: 7 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography
              variant="h2"
              sx={{ mb: '20px', color: '#231F20', fontSize: 20, fontWeight: 600 }}
            >
              {router.locale === 'vi' ? 'Câu hỏi thường gặp' : 'Frequently asked questions'}
            </Typography>
            <Box
              sx={{
                '&:hover a': {
                  backgroundColor: '#EAF9FE',
                  color: '#0058A8 !important',
                },
              }}
            >
              <Link
                href={faqDetailHref}
                style={{
                  color: '#14A8E4',
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  transition: 'all .3s',
                }}
              >
                {router.locale === 'vi' ? 'Xem tất cả' : 'View all'}
                <Image
                  style={{ display: 'inline-block' }}
                  src="/images/chevron-right-blue.svg"
                  alt="chevron-right"
                  width={16}
                  height={16}
                />
              </Link>
            </Box>
          </Box>
          <Grid container spacing={2}>
            {faqGroups.map((group, index) => (
              <Grid item xs={12} md={4} key={group.key}>
                <Box
                  sx={{
                    height: '100%',
                    p: 2,
                    border: '1px solid #E6E6E6',
                    borderRadius: '12px',
                    backgroundColor: '#FAFAFA',
                    boxShadow: '0px 1px 2px 0px #231F200D',
                  }}
                >
                  <Typography
                    sx={{
                      pb: 1,
                      mb: 0.5,
                      borderBottom: '1px solid #E6E6E6',
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#231F20',
                    }}
                  >
                    {group.title ||
                      `${index + 1}. ${
                        router.locale === 'vi' ? 'Câu hỏi thường gặp' : 'Frequently asked questions'
                      }`}
                  </Typography>
                  {group.questions.slice(0, 3).map((question) => (
                    <Link
                      key={question.href}
                      href={question.href}
                      style={{
                        display: 'block',
                        paddingTop: '10px',
                        color: '#231F20',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: 1.6,
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.fontWeight = '500';
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.fontWeight = '400';
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {question.title}
                    </Link>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}

export default DocumentHomePage;
